import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HandCardState } from '@/types/game';

interface UseHandSelectionOptions {
  hand: Record<string, HandCardState>;
  handOrder: string[];
  viewerId: string;
  onFlipHandCard: (cardId: string) => void;
  setHandCardSelection: (cardId: string, selected: boolean) => void;
}

interface UseHandSelectionResult {
  selectedHandCardId: string | null;
  previewHandCardId: string | null;
  previewHandCard: HandCardState | null;
  selectHandCard: (cardId: string | null) => void;
  clearHandSelection: () => void;
  setHoveredHandCardId: React.Dispatch<React.SetStateAction<string | null>>;
  setPreviewHandCardId: React.Dispatch<React.SetStateAction<string | null>>;
}

export function useHandSelection({
  hand,
  handOrder,
  viewerId,
  onFlipHandCard,
  setHandCardSelection
}: UseHandSelectionOptions): UseHandSelectionResult {
  const [selectedHandCardId, setSelectedHandCardId] = useState<string | null>(null);
  const [hoveredHandCardId, setHoveredHandCardId] = useState<string | null>(null);
  const [previewHandCardId, setPreviewHandCardId] = useState<string | null>(null);
  const remoteSelectionRef = useRef<string | null>(null);
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null);

  const previewHandCard = useMemo(() => {
    return previewHandCardId ? hand[previewHandCardId] ?? null : null;
  }, [hand, previewHandCardId]);

  const viewerHandCardIds = useMemo(() => {
    const orderedCardIds = handOrder.filter(cardId => {
      const card = hand[cardId];
      if (!card) {
        return false;
      }

      const ownerId = card.owner ?? viewerId;
      return (ownerId === 'local' ? viewerId : ownerId) === viewerId;
    });

    const orderedCardIdSet = new Set(orderedCardIds);
    const unorderedCardIds = Object.keys(hand).filter(cardId => {
      if (orderedCardIdSet.has(cardId)) {
        return false;
      }

      const card = hand[cardId];
      const ownerId = card.owner ?? viewerId;
      return (ownerId === 'local' ? viewerId : ownerId) === viewerId;
    });

    return [...orderedCardIds, ...unorderedCardIds];
  }, [hand, handOrder, viewerId]);

  const clearHandSelection = useCallback(() => {
    setSelectedHandCardId(null);
    setPreviewHandCardId(null);
  }, []);

  const selectHandCard = useCallback((cardId: string | null) => {
    setSelectedHandCardId(prev => {
      if (!cardId) {
        return null;
      }
      return prev === cardId ? null : cardId;
    });
  }, []);

  useEffect(() => {
    if (selectedHandCardId && !hand[selectedHandCardId]) {
      setSelectedHandCardId(null);
    }
    if (previewHandCardId && !hand[previewHandCardId]) {
      setPreviewHandCardId(null);
    }
    if (hoveredHandCardId && !hand[hoveredHandCardId]) {
      setHoveredHandCardId(null);
    }
  }, [hand, selectedHandCardId, previewHandCardId, hoveredHandCardId]);

  useEffect(() => {
    setPreviewHandCardId(prev => {
      if (!selectedHandCardId) {
        return null;
      }
      if (prev && prev !== selectedHandCardId) {
        return null;
      }
      return prev;
    });
  }, [selectedHandCardId]);

  useEffect(() => {
    const previous = remoteSelectionRef.current;
    if (previous && previous !== selectedHandCardId) {
      const previousCard = hand[previous];
      if (!previousCard || (previousCard.owner ?? viewerId) !== viewerId) {
        setHandCardSelection(previous, false);
      }
      remoteSelectionRef.current = null;
    }

    if (!selectedHandCardId) {
      return;
    }

    const selectedCard = hand[selectedHandCardId];
    if (!selectedCard) {
      return;
    }

    const ownerId = selectedCard.owner ?? viewerId;
    if (ownerId === viewerId) {
      return;
    }

    const alreadySelected = selectedCard.selectedBy?.includes(viewerId) ?? false;
    if (!alreadySelected) {
      setHandCardSelection(selectedCard.id, true);
    }
    remoteSelectionRef.current = selectedCard.id;
  }, [selectedHandCardId, hand, viewerId, setHandCardSelection]);

  useEffect(() => {
    return () => {
      const previous = remoteSelectionRef.current;
      if (previous) {
        setHandCardSelection(previous, false);
        remoteSelectionRef.current = null;
      }
    };
  }, [setHandCardSelection]);

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      lastPointerRef.current = { x: event.clientX, y: event.clientY };
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
    };
  }, []);

  const getHandCardUnderPointer = useCallback(() => {
    const pointer = lastPointerRef.current;
    if (!pointer) {
      return null;
    }

    const element = document.elementFromPoint(pointer.x, pointer.y);
    const handCardElement = element?.closest('[data-hand-card-id]');
    const cardId = handCardElement?.getAttribute('data-hand-card-id') ?? null;
    return cardId && hand[cardId] ? cardId : null;
  }, [hand]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      const key = event.key.toLowerCase();

      if (key === 'escape') {
        event.preventDefault();
        clearHandSelection();
        return;
      }

      if (key === 'z' && previewHandCardId) {
        event.preventDefault();
        setPreviewHandCardId(null);
        return;
      }

      if ((key === 'arrowleft' || key === 'arrowright') && previewHandCardId) {
        const currentIndex = viewerHandCardIds.indexOf(previewHandCardId);
        if (currentIndex === -1 || viewerHandCardIds.length < 2) {
          return;
        }

        event.preventDefault();
        const direction = key === 'arrowright' ? 1 : -1;
        const nextIndex = (currentIndex + direction + viewerHandCardIds.length) % viewerHandCardIds.length;
        setPreviewHandCardId(viewerHandCardIds[nextIndex]);
        setSelectedHandCardId(viewerHandCardIds[nextIndex]);
        return;
      }

      const targetHandCardId = hoveredHandCardId ?? getHandCardUnderPointer() ?? selectedHandCardId;
      if (!targetHandCardId) {
        return;
      }

      const card = hand[targetHandCardId];
      if (!card) {
        return;
      }

      const ownerId = card.owner ?? viewerId;

      if (key === 'f') {
        if (ownerId === viewerId) {
          event.preventDefault();
          onFlipHandCard(targetHandCardId);
        }
        return;
      }

      if (key === 'z') {
        event.preventDefault();
        setPreviewHandCardId(prev => (prev === targetHandCardId ? null : targetHandCardId));
        return;
      }

    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [hoveredHandCardId, selectedHandCardId, previewHandCardId, hand, viewerId, onFlipHandCard, clearHandSelection, getHandCardUnderPointer, viewerHandCardIds]);

  return {
    selectedHandCardId,
    previewHandCardId,
    previewHandCard,
    selectHandCard,
    clearHandSelection,
    setHoveredHandCardId,
    setPreviewHandCardId
  };
}
